/**
 * *"That was a lot!"* — Sneakers suggests a break
 * ([D-160](../../../docs/DECISIONS.md#d-160), mockup 31,
 * [Gamification](../../../docs/GAMIFICATION.md#session-care)).
 *
 * After about 25 minutes of answers with no long pause, and once more at 45,
 * the finish screen is covered by a card that celebrates what was done and
 * suggests a rest. **A suggestion, never a lock**: the second button carries
 * on to the finish screen as if nothing happened, and nothing anywhere stops a
 * child learning.
 *
 * Each suggestion is said once per sitting. Which one was last said is kept in
 * this tab's session storage against the sitting's start, so a new sitting
 * starts fresh and a sibling on the same tablet has their own.
 */

import { breakDue } from '@capytalk/core';
import { useEffect, useState } from 'react';
import type { Client } from './api.js';
import Sneakers from './Sneakers.js';

type Sitting = Awaited<ReturnType<Client['learner']['sitting']['query']>>;

const KEY = 'capytalk.break';

/** The threshold last said for this sitting, if any. */
function shownFor(startedAt: string): number | null {
  try {
    const raw = globalThis.sessionStorage.getItem(KEY);
    if (raw === null) return null;
    const saved = JSON.parse(raw) as { startedAt?: unknown; shown?: unknown };
    return saved.startedAt === startedAt && typeof saved.shown === 'number'
      ? saved.shown
      : null;
  } catch {
    return null;
  }
}

function remember(startedAt: string, shown: number): void {
  try {
    globalThis.sessionStorage.setItem(KEY, JSON.stringify({ startedAt, shown }));
  } catch {
    // Private browsing: the worst case is being asked twice, which is kind.
  }
}

/** Warmer the second time, never a warning either time. */
const WORDS: Readonly<Record<25 | 45, { title: string; line: (m: number) => string }>> =
  {
    25: {
      title: 'That was a lot!',
      line: (minutes) =>
        `You have been learning for ${String(minutes)} minutes. Maybe time for a little break?`,
    },
    45: {
      title: 'What a lot of learning!',
      line: (minutes) =>
        `${String(minutes)} minutes! I am getting sleepy. How about a rest, and more tomorrow?`,
    },
  };

export default function BreakPrompt({
  client,
  onBreak,
}: {
  readonly client: Client;
  /** *Take a break*: the same place *Stop for today* goes. */
  readonly onBreak: () => void;
}) {
  const [sitting, setSitting] = useState<Sitting | null>(null);
  const [due, setDue] = useState<25 | 45 | null>(null);

  useEffect(() => {
    let live = true;
    // An older server has no `sitting`: then there is simply no prompt (D-066).
    Promise.resolve()
      .then(() => client.learner.sitting.query())
      .then((found) => {
        if (!live || found.startedAt === null) return;
        const next = breakDue(found.minutes, shownFor(found.startedAt));
        if (next === null) return;
        remember(found.startedAt, next);
        setSitting(found);
        setDue(next);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [client]);

  if (due === null || sitting === null) return null;
  const words = WORDS[due];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="break-title"
      className="frame"
      style={{ position: 'fixed', inset: 0, zIndex: 20, margin: '0 auto' }}
    >
      <div className="grow center pad">
        <Sneakers text={words.line(sitting.minutes)} audioRef={null} size={120} />
        <h1 id="break-title" style={{ marginTop: 10 }}>
          {words.title}
        </h1>
        <div className="card" style={{ width: '100%', marginTop: 20 }}>
          <div className="row" style={{ gap: 9 }}>
            <div className="stat" style={{ border: 0 }}>
              <b style={{ color: 'var(--gold-ink)' }}>{sitting.answers}</b>
              <span className="tiny">questions answered</span>
            </div>
            <div className="stat" style={{ border: 0 }}>
              <b style={{ color: 'var(--acorn-ink)' }}>{sitting.newWords}</b>
              <span className="tiny">new words</span>
            </div>
          </div>
        </div>
      </div>
      <div className="pad" style={{ paddingBottom: 30 }}>
        <button type="button" className="btn" onClick={onBreak}>
          Take a break <span aria-hidden="true">🌳</span>
        </button>
        <button
          type="button"
          className="btn ghost"
          style={{ marginTop: 10 }}
          onClick={() => {
            setDue(null);
          }}
        >
          I want to keep learning
        </button>
      </div>
    </div>
  );
}
